import type { CallInfo, UpdateCallRequest } from '@compass/client/types/call';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { listDispositionResetFlags } from '@/lib/disposition/resetFlags';
import { useStore } from '@/store/store';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '@/utils/localStorage';

import { getCallQueryKey, useUpdateCall } from './useCalls';

interface UseCallDataEditorOptions {
  caseId?: string;
  selectedCallId?: string | null;
  callDetails?: CallInfo | null;
  outputData?: Record<string, any>;
  /**
   * Whether the working output/disposition draft is mirrored to localStorage.
   * Defaults to `true` (the long-standing behavior used by the Review tab).
   *
   * The Human Call tab passes `false`: its working output holds field VALUES
   * (PHI), so it must NEVER be written to the browser's localStorage (HIPAA).
   * When `false`, the hook performs ZERO localStorage reads/writes for the
   * `callEdit_*_output` / `callEdit_*_disposition` keys; the in-memory
   * `editedCallData` state behavior is unchanged.
   */
  persistToLocalStorage?: boolean;
  /**
   * Called after a successful save that changed the disposition AND carries a
   * numeric code (the API-driven catalog case) — mirrors GlobalDialpad's old
   * post-call Save, which wrote the call's disposition and the case record's
   * disposition_code in one Save press. This hook only owns the call-level
   * write; the caller is expected to persist the case-level disposition_code
   * (e.g. via useUpdateDispositionCode) from this callback.
   */
  onDispositionSaved?: (dispositionCode: number) => void;
  /**
   * Whether the Output section may currently be edited — e.g. the page's
   * `canEdit` gate on `EditableSectionHeader` (latest call, human call type,
   * assigned to the current user, and the case isn't in a state such as
   * human_audit that locks editing). Defaults to `true` so callers that don't
   * pass it (there are none besides the Output section today) keep the prior
   * always-permitted behavior.
   *
   * This is the single source of truth for whether the section may present as
   * "in edit mode" (BI-1848): a stale localStorage draft written while the
   * case was in an editable state (e.g. human_call) must not force
   * `editingCallSections.output` back on once the case moves somewhere
   * editing is locked (e.g. human_audit). The draft is never deleted for
   * this reason alone — restoring it into memory below preserves the
   * agent's unsaved work so it's still there if the section becomes
   * editable again.
   */
  canEdit?: boolean;
  /**
   * The case's workflow_type (e.g. "pbm", "unifiedmedbv"), used to derive
   * which output_data flags a disposition save resets
   * (`listDispositionResetFlags`). Callers that never save a disposition
   * through this hook (the Human Call tab) may omit it — the reset then
   * degrades to the AI-only flags.
   */
  workflowType?: string;
}

interface EditingState {
  output: boolean;
}

export interface DispositionEdit {
  type?: string;
  dispositionType?: number;
  // Free-text call notes. Lives on the call's disposition object, which is why
  // it rides along with the type rather than in its own edit slice.
  notes?: string;
  // Name of the boolean flag in output_data to flip to true when this disposition
  // is saved (e.g. "member_info_mismatch"). Mirrors GlobalDialpad's post-call flow.
  outputDataFlag?: string | null;
}

interface EditedData {
  output: any;
  disposition: DispositionEdit | null;
}

// Merge disposition boolean flags into output_data.
//
// The selected disposition is the source of truth for the workflow's
// disposition flags (`listDispositionResetFlags`): every flag the workflow's
// dispositions can set — plus the AI-only flags — is reset to false, and
// only the one named by `flag` (when the disposition carries one) is set to
// true. This intentionally overrides any flag the user may have manually
// toggled in the output accordion before changing disposition — mirroring
// GlobalDialpad's post-call flow so both entry points produce the same
// output_data shape on save.
function mergeDispositionFlag(
  base: any,
  flag: string | null | undefined,
  workflowType: string | undefined,
): Record<string, any> {
  const resetFlags = Object.fromEntries(
    listDispositionResetFlags(workflowType).map(f => [f, false]),
  );
  return {
    ...(base || {}),
    ...resetFlags,
    ...(flag ? { [flag]: true } : {}),
  };
}

/**
 * Makes `handleCallSave` a disposition-only save, for a layout where something else
 * owns the call's output (the Call Script workspace, BV-2171).
 */
interface CallSaveOptions {
  /**
   * Receives the disposition's output_data flag set/reset (`{ flag: true, other: false }`)
   * so the output's owner can apply it to its own draft. Not called for a notes-only edit.
   */
  applyOutputFlags: (flags: Record<string, unknown>) => void;
}

export interface BuildDispositionPayloadOptions {
  /**
   * The call's currently-saved note (`callDetails?.disposition?.notes`), used
   * to carry the note through when `disposition` doesn't own the `notes` key.
   */
  savedNotes: string | undefined;
  /** The case's workflow_type, scoping which output_data flags get reset. */
  workflowType: string | undefined;
  /**
   * Whether the disposition CHOICE itself changed (type/dispositionType), as
   * opposed to a notes-only edit. Gates the flag reset for dispositions with
   * no mapped flag: switching to a clean outcome (e.g. "Successful
   * Verification") must clear a stale reason flag like `tax_id_required` —
   * the engine's manual-reason priority would otherwise keep overriding the
   * recomputed disposition with it — while merely editing the notes must not
   * touch flags at all.
   */
  dispositionTypeChanged: boolean;
}

// Build the disposition slice of an UpdateCallRequest payload, including the
// output_data flag set/reset whenever the disposition choice changed.
//
// Exported (module-level, no closures) so any write path that needs to save a
// call's disposition in this exact shape can reuse it rather than inventing a
// payload — e.g. StatusConfirmationDialog's disposition picker also writing
// the case's latest human call (BI-1848), alongside this hook's own
// handleCallSave.
export function buildDispositionPayload(
  disposition: DispositionEdit,
  baseOutput: any,
  { savedNotes, workflowType, dispositionTypeChanged }: BuildDispositionPayloadOptions,
): Pick<UpdateCallRequest, 'disposition' | 'disposition_type' | 'output_data'> {
  const { type, dispositionType, notes, outputDataFlag } = disposition;
  // The server REPLACES the disposition subdocument — service/calls.go builds
  // a fresh call.Disposition from the request and $sets the whole thing — so
  // an omitted field is written as empty, not left alone. A draft that does
  // not own `notes` (every draft restored from localStorage, since notes are
  // no longer persisted) therefore has to carry the call's saved note through
  // rather than leave the key out; otherwise saving a disposition change
  // silently erases a note the textarea is still showing.
  const slice: Pick<UpdateCallRequest, 'disposition' | 'disposition_type' | 'output_data'> = {
    disposition: {
      type: type ?? '',
      notes: ('notes' in disposition ? notes : savedNotes) ?? '',
    },
  };
  if (typeof dispositionType === 'number') {
    slice.disposition_type = dispositionType;
  }
  if (outputDataFlag || dispositionTypeChanged) {
    slice.output_data = mergeDispositionFlag(baseOutput, outputDataFlag, workflowType);
  }
  return slice;
}

export function useCallDataEditor({
  caseId,
  selectedCallId,
  callDetails,
  outputData,
  persistToLocalStorage = true,
  onDispositionSaved,
  canEdit = true,
  workflowType,
}: UseCallDataEditorOptions) {
  const queryClient = useQueryClient();
  const updateCall = useUpdateCall();
  const testModeEnabled = useStore(state => state.testModeEnabled);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  // Editing state management
  const [editingCallSections, setEditingCallSections] = useState<EditingState>({
    output: false,
  });

  const [editedCallData, setEditedCallData] = useState<EditedData>({
    output: null,
    disposition: null,
  });

  // Generate localStorage keys
  const getOutputKey = (callId: string | null | undefined) =>
    callId ? `callEdit_${callId}_output` : '';
  const getDispositionKey = (callId: string | null | undefined) =>
    callId ? `callEdit_${callId}_disposition` : '';

  // Handle selectedCallId changes - reset state and restore from localStorage if exists
  useEffect(() => {
    // Always reset state first
    setEditedCallData({ output: null, disposition: null });
    setEditingCallSections({ output: false });

    // Then check if this specific call has saved edits. Skipped entirely when
    // localStorage persistence is disabled (the human call opts out so PHI never
    // touches localStorage — there is nothing to restore).
    if (persistToLocalStorage && selectedCallId && caseId) {
      const outputKey = getOutputKey(selectedCallId);
      const storedOutput = getLocalStorageItem<any>(outputKey);

      if (storedOutput) {
        const dispKey = getDispositionKey(selectedCallId);
        const storedDisposition = getLocalStorageItem<DispositionEdit>(dispKey);
        setEditedCallData({
          output: storedOutput,
          disposition: storedDisposition ?? null,
        });
        // Restore the draft into memory unconditionally — an agent's unsaved
        // work must survive even when editing isn't currently allowed — but
        // only resume edit mode when it actually is (BI-1848 review finding).
        // A draft written earlier while the case was editable (e.g.
        // human_call) must not force the section back into edit mode after
        // the case moves somewhere editing is locked (e.g. human_audit).
        setEditingCallSections({ output: canEdit });
      }
    }
  }, [selectedCallId, caseId, persistToLocalStorage, canEdit]);

  // Build a disposition snapshot from a call record
  const snapshotDisposition = (call: CallInfo | null | undefined): DispositionEdit => ({
    type: call?.disposition?.type,
    dispositionType: call?.disposition_type,
    notes: call?.disposition?.notes,
  });

  // The subset of a disposition draft that may be mirrored to localStorage.
  //
  // `notes` is free text the agent typed about a patient's call — PHI — and
  // localStorage is not somewhere PHI may live (see ui/dashboard/CLAUDE.md;
  // it is the same reason the Human Call tab opts out of persistence
  // wholesale). It stays in the in-memory draft, so editing is unaffected;
  // only a draft restored after a reload comes back without it, which
  // buildDispositionPayload handles by carrying the saved note through.
  const persistableDisposition = (disposition: DispositionEdit | null): DispositionEdit | null =>
    disposition && {
      type: disposition.type,
      dispositionType: disposition.dispositionType,
      outputDataFlag: disposition.outputDataFlag,
    };

  // Handler to start editing a section (only output supported now)
  const handleCallEditStart = useCallback(
    (section: 'output') => {
      const currentData = outputData;
      const newEditingSections = { ...editingCallSections, [section]: true };
      const newEditedData: EditedData = {
        ...editedCallData,
        [section]: JSON.parse(JSON.stringify(currentData || {})),
        disposition: snapshotDisposition(callDetails),
      };

      setEditingCallSections(newEditingSections);
      setEditedCallData(newEditedData);

      // Save to localStorage
      if (persistToLocalStorage && selectedCallId) {
        setLocalStorageItem(getOutputKey(selectedCallId), newEditedData[section]);
        setLocalStorageItem(
          getDispositionKey(selectedCallId),
          persistableDisposition(newEditedData.disposition),
        );
      }
    },
    [
      outputData,
      editingCallSections,
      editedCallData,
      selectedCallId,
      callDetails,
      persistToLocalStorage,
    ],
  );

  // Handler to cancel editing a section (only output supported now)
  const handleCallEditCancel = useCallback(
    (section: 'output') => {
      const newEditingSections = { ...editingCallSections, [section]: false };
      setEditingCallSections(newEditingSections);
      setEditedCallData({ ...editedCallData, [section]: null, disposition: null });

      // Clear localStorage for this section
      if (persistToLocalStorage && selectedCallId) {
        removeLocalStorageItem(getOutputKey(selectedCallId));
        removeLocalStorageItem(getDispositionKey(selectedCallId));
      }
    },
    [editingCallSections, editedCallData, selectedCallId, persistToLocalStorage],
  );

  // Helper function to clear state and localStorage after save
  const clearSectionData = useCallback(
    (section: 'output') => {
      // Update the local state
      setEditingCallSections(prev => ({ ...prev, [section]: false }));
      setEditedCallData(prev => ({ ...prev, [section]: null, disposition: null }));

      // Clear localStorage after successful save
      if (persistToLocalStorage && selectedCallId) {
        removeLocalStorageItem(getOutputKey(selectedCallId));
        removeLocalStorageItem(getDispositionKey(selectedCallId));
      }
    },
    [selectedCallId, persistToLocalStorage],
  );

  // Helper to check whether the edited disposition differs from the saved one
  const hasDispositionChanged = useCallback(
    (edited: DispositionEdit | null, call: CallInfo | null | undefined): boolean => {
      if (!edited) return false;
      const current = snapshotDisposition(call);
      return (
        edited.type !== current.type ||
        edited.dispositionType !== current.dispositionType ||
        // A draft that does not own the `notes` key never edited the notes, so
        // its absence is not a change. Restored drafts are exactly that: notes
        // are deliberately not persisted (PHI), and drafts written before this
        // branch had no notes key at all.
        ('notes' in edited && edited.notes !== current.notes)
      );
    },
    [],
  );

  // Helper function to prepare data for update (only output supported)
  const prepareUpdateData = (data: any): UpdateCallRequest => {
    return {
      output_data: data,
    };
  };

  // Helper to handle post-save operations
  const handlePostSave = useCallback(() => {
    clearSectionData('output');
    if (selectedCallId) {
      queryClient.invalidateQueries({
        queryKey: getCallQueryKey(
          selectedOrganizationId,
          selectedProgramId,
          caseId,
          selectedCallId,
          testModeEnabled,
        ),
      });
    }
    toast.success('Call output data updated successfully');
  }, [
    clearSectionData,
    queryClient,
    testModeEnabled,
    selectedCallId,
    selectedOrganizationId,
    selectedProgramId,
    caseId,
  ]);

  // Helper to handle save errors
  const handleSaveError = (error: any, defaultMessage: string) => {
    const errorMessage = error?.response?.data?.message || error?.message || defaultMessage;
    toast.error(errorMessage);
  };

  // Helper to check if we can perform call updates
  const canPerformCallUpdate = (extractedData?: Record<string, unknown> | null): boolean => {
    if (!caseId || !selectedCallId || !callDetails) return false;
    if (extractedData !== undefined && (!extractedData || !outputData)) return false;
    return true;
  };

  // The numeric disposition code to persist onto the case record once the
  // save that carries it succeeds, or undefined when there is nothing to
  // persist. Mirrors the old dialpad's `selectedDispositionCode !== null`
  // guard — only the API-driven catalog case carries a numeric code.
  const dispositionCodeToPersist = (
    changed: boolean,
    disposition: DispositionEdit | null,
  ): number | undefined => {
    const code = changed ? disposition?.dispositionType : undefined;
    return typeof code === 'number' ? code : undefined;
  };

  // Notifies the case-level disposition_code write, only when there is one.
  const notifyDispositionSaved = (code: number | undefined) => {
    if (code === undefined) return;
    onDispositionSaved?.(code);
  };

  // Whether the disposition CHOICE differs from the saved one — the notes
  // deliberately don't count. hasDispositionChanged above answers "should
  // this save carry a disposition payload at all?" (notes included); this
  // answers "did the outcome itself change?", which is what gates the
  // output_data flag reset — a notes-only edit must never touch flags.
  const hasDispositionTypeChanged = (
    edited: DispositionEdit,
    call: CallInfo | null | undefined,
  ): boolean => {
    const current = snapshotDisposition(call);
    return edited.type !== current.type || edited.dispositionType !== current.dispositionType;
  };

  // Wraps the exported buildDispositionPayload with this hook's own
  // `callDetails` closure — kept as its own function (rather than inlined in
  // handleCallSave below) so the optional-chained `notes` lookup doesn't push
  // handleCallSave over the lint complexity limit.
  const buildDispositionPayloadForCall = (
    disposition: DispositionEdit,
    baseOutput: any,
  ): Pick<UpdateCallRequest, 'disposition' | 'disposition_type' | 'output_data'> =>
    buildDispositionPayload(disposition, baseOutput, {
      savedNotes: callDetails?.disposition?.notes,
      workflowType,
      dispositionTypeChanged: hasDispositionTypeChanged(disposition, callDetails),
    });

  // Call Script layout Save (BV-2171): write the disposition and NOTHING else.
  //
  // In that layout the fields are captured in the Call Script workspace, a separate draft
  // that autosaves the call's output on every edit. This hook's own output draft is only
  // the snapshot `handleCallEditStart` took when Edit was pressed — Edit is what enables
  // the disposition select — so it never sees a single script edit. Posting it (as the
  // accordion layout's Save does) replaced the call's output with that snapshot and, via
  // the call→case mirror, reverted every field captured since Edit on the case too.
  //
  // The disposition's output_data flags are still part of the disposition, so they are
  // handed to `applyOutputFlags` — the workspace, which owns the output — rather than
  // posted from here. Applied only after the disposition write succeeds, so a failed save
  // leaves the flags and the saved disposition in agreement.
  const saveDispositionOnly = async ({ applyOutputFlags }: CallSaveOptions) => {
    const disposition = editedCallData.disposition;
    if (!disposition || !hasDispositionChanged(disposition, callDetails)) {
      // Nothing of ours to write; drop the Edit-time snapshot and leave edit mode.
      clearSectionData('output');
      return;
    }

    // An empty base makes `output_data` exactly the flag set/reset for this choice (or
    // absent for a notes-only edit) — never a copy of any field value.
    const { output_data: flags, ...dispositionSlice } = buildDispositionPayloadForCall(
      disposition,
      {},
    );

    try {
      await updateCall.mutateAsync({
        caseRecordId: caseId!,
        callId: selectedCallId!,
        data: dispositionSlice,
      });
    } catch (error: any) {
      handleSaveError(error, 'Failed to update call disposition');
      return;
    }
    if (flags) applyOutputFlags(flags);
    handlePostSave();
    notifyDispositionSaved(dispositionCodeToPersist(true, disposition));
  };

  // Handler to save edited data (output + disposition). With `options`, saves the
  // disposition only and routes its output flags to the caller — see saveDispositionOnly.
  const handleCallSave = useCallback(
    async (options?: CallSaveOptions) => {
      if (!canPerformCallUpdate()) return;
      if (options) {
        await saveDispositionOnly(options);
        return;
      }

      const outputChanged = editedCallData.output !== null;
      const dispositionChanged = hasDispositionChanged(editedCallData.disposition, callDetails);

      if (!outputChanged && !dispositionChanged) return;

      const payload: UpdateCallRequest = {};
      if (outputChanged) {
        Object.assign(payload, prepareUpdateData(editedCallData.output));
      }
      if (dispositionChanged && editedCallData.disposition) {
        const baseOutput = outputChanged ? editedCallData.output : outputData;
        Object.assign(
          payload,
          buildDispositionPayloadForCall(editedCallData.disposition, baseOutput),
        );
      }

      // Captured before handlePostSave clears the draft below.
      const savedDispositionCode = dispositionCodeToPersist(
        dispositionChanged,
        editedCallData.disposition,
      );

      try {
        await updateCall.mutateAsync({
          caseRecordId: caseId!,
          callId: selectedCallId!,
          data: payload,
        });
        handlePostSave();
        notifyDispositionSaved(savedDispositionCode);
      } catch (error: any) {
        handleSaveError(error, 'Failed to update call output data');
      }
    },
    [
      caseId,
      selectedCallId,
      callDetails,
      editedCallData,
      outputData,
      updateCall,
      handlePostSave,
      onDispositionSaved,
    ],
  );

  // Handler to update edited data (only output supported now)
  const handleDataChange = useCallback(
    (section: 'output', newData: any) => {
      setEditedCallData(prev => ({ ...prev, [section]: newData }));

      // Save to localStorage
      if (persistToLocalStorage && selectedCallId) {
        setLocalStorageItem(getOutputKey(selectedCallId), newData);
      }
    },
    [selectedCallId, persistToLocalStorage],
  );

  // Handler to update edited disposition. Merges `next` into the existing
  // draft rather than replacing it: each caller (the disposition dropdown,
  // the notes textarea) only passes the field(s) it owns, so a full replace
  // silently dropped whatever the other caller had already set — e.g.
  // changing only the dropdown blanked an already-typed note (BI-1848
  // review). A field explicitly set to `undefined` (clearing the
  // disposition) still overwrites, since object spread keeps own properties
  // regardless of value.
  const handleDispositionChange = useCallback(
    (next: DispositionEdit) => {
      setEditedCallData(prev => {
        const merged: DispositionEdit = { ...(prev.disposition ?? {}), ...next };
        if (persistToLocalStorage && selectedCallId) {
          setLocalStorageItem(getDispositionKey(selectedCallId), persistableDisposition(merged));
        }
        return { ...prev, disposition: merged };
      });
    },
    [selectedCallId, persistToLocalStorage],
  );

  // Belt-and-braces (BI-1848): every consumer of `editingCallSections.output`
  // reads it through this single return value, so clamping it here — on top
  // of gating the restore effect above — means the section can never present
  // as "in edit mode" while `canEdit` is false, regardless of how the
  // internal state got set (now, or by some future code path). It also means
  // editing resumes automatically, with the preserved draft, the moment
  // `canEdit` flips back to true without requiring a call/case reselect.
  const exposedEditingCallSections: EditingState = {
    output: editingCallSections.output && canEdit,
  };

  return {
    // State
    editingCallSections: exposedEditingCallSections,
    editedCallData,
    isSaving: updateCall.isPending,

    // Handlers
    handleCallEditStart,
    handleCallEditCancel,
    handleCallSave,
    handleDataChange,
    handleDispositionChange,

    // Utils
    setEditedCallData,
  };
}
